// src/App.js
import React, { useState, useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from "firebase/firestore";

// Pages & components
import SignupPage from "./pages/SignupPage";
import ContactPage from "./pages/ContactPage";
import StudentDashboard from "./pages/StudentDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import Login from "./pages/Login";
import Navbar from "./components/Navbar";
import ArchiveViewer from "./pages/ArchiveViewer";
import SchoolSignup from "./pages/SchoolSignup";
import LandingPage from "./pages/LandingPage";
import Footer from "./components/Footer";
import DemoPage from "./pages/DemoPage";
import SchoolRoutes from "./SchoolRoutes";
import StoreItemLabels from "./pages/StoreItemLabels";
import AboutPage from "./pages/AboutPage";
import StudentStats from "./pages/StudentStats";
import StaffStats from "./pages/StaffStats";
import FeaturesPage from "./pages/FeaturesPage";
import PlatformAdmin from "./pages/PlatformAdmin";
import SuccessPage from "./pages/SuccessPage";
import PaymentRequired from "./pages/PaymentRequired";
import WhatIsPBIS from "./pages/WhatIsPBIS";
import AccountPage from "./pages/AccountPage";

const RequireSchoolAccess = ({ user, userSchoolId, role, allow, children }) => {
  const { schoolId: routeSchoolId } = useParams();

  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!userSchoolId || routeSchoolId !== userSchoolId) {
    return <Navigate to={userSchoolId ? `/${userSchoolId}` : "/"} replace />;
  }
  if (allow && !allow.includes(role)) {
    return <Navigate to={`/${userSchoolId}`} replace />;
  }
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [advisory, setAdvisory] = useState(null);
  const [schoolId, setSchoolId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (loggedInUser) => {
      if (!loggedInUser) {
        setUser(null);
        setRole(null);
        setAdvisory(null);
        setSchoolId(null);
        setAuthChecked(true);
        return;
      }

      setUser(loggedInUser);

      try {
        let resolvedDocRef = doc(db, "users", loggedInUser.uid);
        let snap = await getDoc(resolvedDocRef);


        if (snap.exists()) {
          const data = snap.data();

          const updates = {};
          if (data.uid !== loggedInUser.uid) updates.uid = loggedInUser.uid;
          if (!data.email && loggedInUser.email) updates.email = loggedInUser.email;
          if (!data.name && loggedInUser.displayName) {
            updates.name = loggedInUser.displayName;
          }

          if (Object.keys(updates).length > 0) {
            await updateDoc(resolvedDocRef, updates);
          }

          const refreshedSnap = await getDoc(resolvedDocRef);
          const refreshedData = refreshedSnap.data();

          const resolvedRole = refreshedData.role ?? "student";
          const resolvedClass =
            refreshedData.class ?? refreshedData.advisory ?? "Unassigned";
          const resolvedSchool = refreshedData.schoolId ?? null;
          setRole(resolvedRole);
          setAdvisory(resolvedClass);
          setSchoolId(resolvedSchool);
// Skip payment check for platform admins
const isPlatformAdmin = [
  "rmelhus@nlcschool.org",
  "mjnayr@yahoo.com"
].includes(loggedInUser.email?.toLowerCase());

if (resolvedSchool && !isPlatformAdmin) {            const schoolRef = doc(db, "schools", resolvedSchool);
            const schoolSnap = await getDoc(schoolRef);
          
            if (schoolSnap.exists()) {
              const schoolData = schoolSnap.data();
          
              const isOnSuccessPage = window.location.pathname === "/success";

if (schoolData.paymentStatus !== "active" && !isOnSuccessPage) {
  setAuthChecked(true);
  navigate(`/payment-required?schoolId=${resolvedSchool}`);
  return;
}
            }
          }

          console.log("👤 Firestore user:", {
            authUid: loggedInUser.uid,
            docId: resolvedDocRef.id,
            role: resolvedRole,
            classOrAdvisory: resolvedClass,
            schoolId: resolvedSchool,
          });
        } else {
          // Only seed if we truly cannot find an existing profile anywhere
          const newUserRef = doc(db, "users", loggedInUser.uid);
          await setDoc(newUserRef, {
            uid: loggedInUser.uid,
            id: loggedInUser.uid,
            name: loggedInUser.displayName || "",
            email: loggedInUser.email || "",
            role: "student",
            advisory: "Unassigned",
            class: "Unassigned",
            schoolId: null,
            createdAt: new Date(),
          });

          setRole("student");
          setAdvisory("Unassigned");
          setSchoolId(null);
          console.log("🆕 Seeded default user doc (student).");
        }
        
        setAuthChecked(true);

      } catch (err) {
        console.error("🔥 Error resolving user role:", err);
        setRole("student");
        setAdvisory("Unassigned");
        setSchoolId(null);
        setAuthChecked(true);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setRole(null);
      setAdvisory(null);
      setSchoolId(null);
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const publicMarketingRoutes = {
    "/": <LandingPage />,
    "/features": <FeaturesPage />,
    "/about": <AboutPage />,
    "/what-is-pbis": <WhatIsPBIS />,
    "/contact": <ContactPage />,
  };
  const publicMarketingPage = publicMarketingRoutes[location.pathname];

  // Public pages do not depend on a signed-in user. Rendering them immediately
  // keeps their content and Helmet metadata from waiting on Firebase Auth.
  if (!authChecked && publicMarketingPage) {
    return publicMarketingPage;
  }

  if (!authChecked) {
    return <div>Loading...</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {user && (
  <Navbar
    user={user}
    role={role}
    schoolId={schoolId}
    handleSignOut={handleSignOut}
  />
)}

      <div style={{ flex: 1 }}>
          <Routes>
            <Route path="/school-signup" element={<SchoolSignup />} />
            <Route path="/redemption-cards" element={
              <Navigate to={schoolId ? `/${schoolId}/redemption-cards` : "/login"} replace />
            } />
            <Route path="/:schoolId/store-labels" element={
              <RequireSchoolAccess user={user} userSchoolId={schoolId} role={role} allow={["admin"]}>
                <StoreItemLabels />
              </RequireSchoolAccess>
            } />
            <Route path="/print-store-qr" element={
              <Navigate to={schoolId ? `/${schoolId}/print-store-qr` : "/login"} replace />
            } />
            <Route path="/store-mode" element={
              <Navigate
                to={schoolId ? `/${schoolId}/store-mode${location.search}` : "/login"}
                replace
              />
            } />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/archives" element={
              <Navigate to={schoolId ? `/${schoolId}/archives` : "/login"} replace />
            } />
            <Route path="/:schoolId/student-stats" element={
              <RequireSchoolAccess user={user} userSchoolId={schoolId} role={role} allow={["admin", "staff"]}>
                <StudentStats />
              </RequireSchoolAccess>
            } />
            <Route path="/:schoolId/staff-stats" element={
              <RequireSchoolAccess user={user} userSchoolId={schoolId} role={role} allow={["admin", "staff"]}>
                <StaffStats />
              </RequireSchoolAccess>
            } />
            <Route path="/:schoolId/account" element={
              <RequireSchoolAccess user={user} userSchoolId={schoolId} role={role} allow={["admin"]}>
                <AccountPage />
              </RequireSchoolAccess>
            } />
            <Route path="/student/store" element={
              <Navigate to={schoolId ? `/${schoolId}/store` : "/login"} replace />
            } />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/platform-admin" element={<PlatformAdmin />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/payment-required" element={<PaymentRequired />} />
            <Route path="/what-is-pbis" element={<WhatIsPBIS />} />
            <Route path="/:schoolId/archives" element={
              <RequireSchoolAccess user={user} userSchoolId={schoolId} role={role} allow={["admin"]}>
                <ArchiveViewer />
              </RequireSchoolAccess>
            } />
            <Route
              path="/:schoolId/*"
              element={
                <RequireSchoolAccess user={user} userSchoolId={schoolId} role={role}>
                  <SchoolRoutes user={user} role={role} advisory={advisory} />
                </RequireSchoolAccess>
              }
            />

            <Route
              path="/"
              element={
                !user ? (
                  <LandingPage />
                ) : role === "admin" || role === "staff" ? (
                  <AdminDashboard />
                ) : (
                  <StudentDashboard advisory={advisory} />
                )
              }
            />
          </Routes>
      </div>

      <Footer />
    </div>
  );
}

export default App;
